import { ColDef } from "ag-grid-community";
import ProgressIndicatorModel from "../../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import { PlacesField } from "../../../../../../enums";
import GridToastService from "../../../../../../services/local/gridToastService/GridToastService";
import { BaseGridColumnBuilder } from "../base/BaseGridColumnBuilder";
import PlacesApi from "../../../../../../services/api/v2/places/Places.api";
import { CommonColDefFieldNamesEnum, PlacesColDefFieldNamesEnum } from "../../../enums/AgGridColDefFieldNameEnum";
import { NameColumnBuilder } from "../../columns/commonColumns/NameColumn/NameColumn_builder";
import I18n from "../../../../../localization/I18n";
import { MasterDetailRowColumnBuilder } from "../../columns/commonColumns/MasterDetailRowColumn/MasterDetailRowColumn_builder";
import { NAME_COLUMN_CONFIG, NAME_FILTER_CONFIG } from "../../columns/commonColumns/NameColumn/NameColumn_config";
import { DATE_FILTER_CONFIG } from "../../columns/commonColumns/DateColumn/DateColumn_config";
import { DateColumnBuilder } from "../../columns/commonColumns/DateColumn/DateColumn_builder";
import { SelectionColumnBuilder } from "../../columns/commonColumns/SelectionColumn/SelectionColumn_builder";
import { SimpleTextColumnBuilder } from "../../columns/commonColumns/SimpleTextColumn/SimpleTextColumn_builder";
import { SIMPLE_TEXT_COLUMN_CONFIG } from "../../columns/commonColumns/SimpleTextColumn/SimpleTextColumn_config";
import { BASE_FILTER_CONFIG } from "../../columns/baseColumn/BaseColumn_config";

export interface PlacesGridColumnBuilderProps {
  canEdit: boolean;
  organisationId: number;
  columns?: string[];
  onFieldUpdate: () => void;
  //   deletePlacesFromGroup: any;
}

export class PlacesGridColumnBuilder extends BaseGridColumnBuilder {
  gridColumns: Dictionary<ColDef>;
  gridToastService = GridToastService;
  httpProgress = ProgressIndicatorModel;
  gridProps: PlacesGridColumnBuilderProps;
  columnDefs: Dictionary<() => ColDef>;
  organisationId: number;
  onFieldUpdate: () => void;
  //   deletePlacesFromGroup: any;

  constructor(gridProps: PlacesGridColumnBuilderProps) {
    super(PlacesApi.updatePlaceField, gridProps.organisationId, null, gridProps.canEdit);
    this.gridProps = gridProps;
    this.organisationId = gridProps.organisationId;
    // this.deletePlacesFromGroup - gridProps.deletePlacesFromGroup;
    this.onFieldUpdate = gridProps.onFieldUpdate;
    this.init();
  }

  private init = () => {
    this.columnDefs = {
      [CommonColDefFieldNamesEnum.Selected]: () =>
        new SelectionColumnBuilder().makeSelectable().generateColumnOptions(),
      [CommonColDefFieldNamesEnum.Name]: () => this.buildNameColumn(),
      [PlacesColDefFieldNamesEnum.StreetAddress]: () => this.buildStreetAddressColumn(),
      [PlacesColDefFieldNamesEnum.City]: () => this.buildCityColumn(),
      [PlacesColDefFieldNamesEnum.Country]: () => this.buildCountryColumn(),
      [PlacesColDefFieldNamesEnum.Latitude]: () => this.buildLatitudeColumn(),
      [PlacesColDefFieldNamesEnum.Longitude]: () => this.buildLongitudeColumn(),
      [CommonColDefFieldNamesEnum.CreatedBy]: () =>
        new NameColumnBuilder({
          field: CommonColDefFieldNamesEnum.CreatedBy,
          headerName: I18n.t("grids.createdBy"),
          pinned: false
        })
          .makeEditable(false)
          .makeReadOnly()
          .generateColumnOptions(),
      [CommonColDefFieldNamesEnum.CreatedAt]: () =>
        new DateColumnBuilder({ field: CommonColDefFieldNamesEnum.CreatedAt, headerName: I18n.t("grids.createdOn") })
          .makeEditable(false)
          .makeReadOnly()
          .withCellEditor(CommonColDefFieldNamesEnum.CreatedAt, "")
          .withComparator()
          .setValueFormatter(CommonColDefFieldNamesEnum.CreatedAt)
          .setFilterOptions(DATE_FILTER_CONFIG)
          .generateColumnOptions(),
      [CommonColDefFieldNamesEnum.ModifiedBy]: () =>
        new NameColumnBuilder({
          field: CommonColDefFieldNamesEnum.ModifiedBy,
          headerName: I18n.t("grids.lastModifiedBy"),
          pinned: false
        })
          .makeEditable(false)
          .makeReadOnly()
          .generateColumnOptions(),
      [CommonColDefFieldNamesEnum.UpdatedAt]: () =>
        new DateColumnBuilder({
          field: CommonColDefFieldNamesEnum.UpdatedAt,
          headerName: I18n.t("grids.lastModifiedOn")
        })
          .makeEditable(false)
          .makeReadOnly()
          .withCellEditor(CommonColDefFieldNamesEnum.UpdatedAt, "")
          .withComparator()
          .setValueFormatter(CommonColDefFieldNamesEnum.UpdatedAt)
          .setFilterOptions(DATE_FILTER_CONFIG)
          .generateColumnOptions()
    };
  };

  generateColumnDefs = (): ColDef[] => {
    let res: ColDef[] = [];

    this.gridProps.columns.forEach(e => {
      const col = this.columnDefs[e];
      if (typeof col === "function") {
        res.push(col());
      } else {
        console.warn(`Typeof columnDef with name "${this.columnDefs}" is not a function`);
      }
    });

    return res;
  };

  buildNameColumn = () => {
    let model = new MasterDetailRowColumnBuilder({
      field: CommonColDefFieldNamesEnum.Name,
      headerName: I18n.t("phrases.name"),
      pinned: "left",
      resizable: true,
      width: 100
    })
      .setColumnOptions(NAME_COLUMN_CONFIG({ headerName: "Name" }))
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(false)
      .makeReadOnly()
      .setFilterOptions(NAME_FILTER_CONFIG);

    if (this.gridProps.canEdit) {
      model.useExpandedMasterHeaderHighlighting([CommonColDefFieldNamesEnum.Name]).createValueSetter(this.updateName);
    }
    return model.generateColumnOptions();
  };
  buildStreetAddressColumn = () => {
    let model = new SimpleTextColumnBuilder()
      .setColumnOptions(
        SIMPLE_TEXT_COLUMN_CONFIG({
          field: PlacesColDefFieldNamesEnum.StreetAddress,
          headerName: I18n.t("grids.streetAddress")
        })
      )
      .makeSelectable(this.canEdit)
      .makeEditable(false)
      .makeReadOnly()
      .setFilterOptions(BASE_FILTER_CONFIG);

    if (this.canEdit) {
      model.createValueSetter(this.updateStreetAddress);
    }

    if (this.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };
  buildCityColumn = () => {
    let model = new SimpleTextColumnBuilder()
      .setColumnOptions(
        SIMPLE_TEXT_COLUMN_CONFIG({
          field: PlacesColDefFieldNamesEnum.City,
          headerName: I18n.t("grids.city")
        })
      )
      .makeSelectable(this.canEdit)
      .makeEditable(false)
      .makeReadOnly()
      .setFilterOptions(BASE_FILTER_CONFIG);

    if (this.canEdit) {
      model.createValueSetter(this.updateCity);
    }

    if (this.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };
  buildCountryColumn = () => {
    let model = new SimpleTextColumnBuilder()
      .setColumnOptions(
        SIMPLE_TEXT_COLUMN_CONFIG({
          field: PlacesColDefFieldNamesEnum.Country,
          headerName: I18n.t("grids.country")
        })
      )
      .makeSelectable(this.canEdit)
      .makeEditable(false)
      .makeReadOnly()
      .setFilterOptions(BASE_FILTER_CONFIG);

    if (this.canEdit) {
      model.createValueSetter(this.updateCountry);
    }

    if (this.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };
  buildLatitudeColumn = () => {
    let model = new SimpleTextColumnBuilder()
      .setColumnOptions(
        SIMPLE_TEXT_COLUMN_CONFIG({
          field: PlacesColDefFieldNamesEnum.Latitude,
          headerName: I18n.t("grids.latitude")
        })
      )
      .makeSelectable(this.canEdit)
      .makeEditable(false)
      .makeReadOnly()
      .setFilterOptions(BASE_FILTER_CONFIG);

    if (this.canEdit) {
      model.createValueSetter(this.updateLatitude);
    }

    if (this.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };
  buildLongitudeColumn = () => {
    let model = new SimpleTextColumnBuilder()
      .setColumnOptions(
        SIMPLE_TEXT_COLUMN_CONFIG({
          field: PlacesColDefFieldNamesEnum.Longitude,
          headerName: I18n.t("grids.longitude")
        })
      )
      .makeSelectable(this.canEdit)
      .makeEditable(false)
      .makeReadOnly()
      .setFilterOptions(BASE_FILTER_CONFIG);

    if (this.canEdit) {
      model.createValueSetter(this.updateLongitude);
    }

    if (this.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  updateName = async (entityId: number, text: string) => {
    await this.updateTextField(PlacesField.name, entityId, text);
    this.onFieldUpdate();
  };
  updateStreetAddress = async (entityId: number, text: string) => {
    await this.updateTextField(PlacesField.streetAddress, entityId, text);
    this.onFieldUpdate();
  };
  updateCity = async (entityId: number, text: string) => {
    await this.updateTextField(PlacesField.city, entityId, text);
    this.onFieldUpdate();
  };
  updateCountry = async (entityId: number, text: string) => {
    await this.updateTextField(PlacesField.country, entityId, text);
    this.onFieldUpdate();
  };
  updateLatitude = async (entityId: number, text: string) => {
    await this.updateTextField(PlacesField.latitude, entityId, text);
    this.onFieldUpdate();
  };
  updateLongitude = async (entityId: number, text: string) => {
    await this.updateTextField(PlacesField.longitude, entityId, text);
    this.onFieldUpdate();
  };
}
